import { useEffect, useRef, useState } from "react";
import { FileText, Film, Link2, Loader2, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { uploadChunked } from "@/lib/upload";
import { ImageUpload } from "@/components/DesignPanel";

const MAX = 20;
const inputCls = "w-full h-10 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const KINDS = [["pdf", FileText, "PDF"], ["video_upload", Film, "Video file"], ["video_url", Link2, "Video link"]];
const EMPTY = { kind: "pdf", title: "", description: "", thumbnail_path: "", file_path: "", filename: "", video_path: "", video_filename: "", video_url: "" };
const kindOf = (a) => (a.campaign_type === "whitepaper" ? "PDF" : a.video_type === "upload" ? "Video file" : "Video link");

const AddForm = ({ pid, onDone, onCancel }) => {
  const [f, setF] = useState(EMPTY);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const upload = async (file) => {
    if (!file) return;
    const pdf = f.kind === "pdf";
    if (pdf ? file.type !== "application/pdf" : !file.type.startsWith("video/")) return toast.error(pdf ? "Please choose a PDF file" : "Please choose a video file");
    setProgress(0);
    try {
      const d = await uploadChunked(file, setProgress);
      const name = file.name.replace(/\.[^.]+$/, "");
      set(pdf ? { file_path: d.path, filename: d.filename, title: f.title || name } : { video_path: d.path, video_filename: d.filename, title: f.title || name });
    } catch (e) { toast.error(formatErr(e)); } finally { setProgress(null); if (ref.current) ref.current.value = ""; }
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/programs/${pid}/assets`, f);
      if (data.hub_warning) toast.warning(data.hub_warning); else toast.success("Added to the hub");
      onDone();
    }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const file = f.kind === "pdf" ? f.filename : f.video_filename;
  return (
    <div className="rounded-md border border-indigo-500/40 bg-[#0B0F17]/60 p-5 space-y-4" data-testid="hub-asset-form">
      <div className="flex gap-1 p-1 rounded-lg bg-[#0B0F17] border border-[#242C3D]">
        {KINDS.map(([k, Icon, label]) => (
          <button key={k} type="button" data-testid={`hub-asset-kind-${k}`} onClick={() => set({ kind: k })}
            className={`flex-1 h-9 rounded-md flex items-center justify-center gap-2 text-sm transition-colors ${f.kind === k ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-white"}`}><Icon className="w-4 h-4" /> {label}</button>
        ))}
      </div>
      {f.kind === "video_url" ? (
        <input data-testid="hub-asset-url-input" value={f.video_url} onChange={(e) => set({ video_url: e.target.value })} className={inputCls} placeholder="https://youtube.com/watch?v=…  ·  vimeo.com/…  ·  https://…/video.mp4" />
      ) : (
        <>
          <input ref={ref} type="file" className="hidden" data-testid="hub-asset-file-input" accept={f.kind === "pdf" ? "application/pdf" : "video/*"} onChange={(e) => upload(e.target.files[0])} />
          <button type="button" data-testid="hub-asset-upload-button" disabled={progress !== null} onClick={() => ref.current?.click()}
            className="w-full rounded-lg border border-dashed border-[#2f3a50] hover:border-indigo-500 py-5 flex items-center justify-center gap-2 text-sm text-slate-300 transition-colors">
            {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</> : file ? <span data-testid="hub-asset-file-name">{file} · click to replace</span> : <><Upload className="w-4 h-4" /> Upload {f.kind === "pdf" ? "PDF" : "video (MP4/WebM)"}</>}
          </button>
        </>
      )}
      <input data-testid="hub-asset-title-input" value={f.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} placeholder="Title *" maxLength={200} />
      <input data-testid="hub-asset-desc-input" value={f.description} onChange={(e) => set({ description: e.target.value })} className={inputCls} placeholder="Short description" maxLength={500} />
      <div><p className="text-xs text-slate-500 mb-2">Thumbnail (optional)</p><ImageUpload value={f.thumbnail_path} onChange={(p) => set({ thumbnail_path: p })} testid="hub-asset-thumb-upload" emptyLabel="Upload thumbnail" /></div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="hub-asset-cancel" className="h-9 px-4 rounded-full border border-[#242C3D] text-sm hover:bg-white/5">Cancel</button>
        <button type="button" onClick={save} disabled={busy || progress !== null || !f.title.trim()} data-testid="hub-asset-save"
          className="h-9 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-50 flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Add to hub</button>
      </div>
    </div>
  );
};

const AssetRow = ({ pid, a, onChanged }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try { await api.put(`/programs/${pid}/assets/${a.id}`, edit); toast.success("Saved"); setEdit(null); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Remove "${a.title}" from the hub? Its views and leads are deleted too.`)) return;
    try { await api.delete(`/campaigns/${a.id}`); toast.success("Removed"); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <li className="rounded-md border border-[#242C3D] bg-[#0B0F17]/60 p-4" data-testid={`hub-asset-row-${a.id}`}>
      {edit ? (
        <div className="space-y-3">
          <input data-testid={`hub-asset-edit-title-${a.id}`} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={inputCls} />
          <input data-testid={`hub-asset-edit-desc-${a.id}`} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={inputCls} placeholder="Short description" />
          <ImageUpload value={edit.thumbnail_path} onChange={(p) => setEdit({ ...edit, thumbnail_path: p })} testid={`hub-asset-edit-thumb-${a.id}`} emptyLabel="Upload thumbnail" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEdit(null)} className="h-8 px-3 rounded-full border border-[#242C3D] text-xs"><X className="w-3.5 h-3.5" /></button>
            <button type="button" data-testid={`hub-asset-edit-save-${a.id}`} onClick={save} className="h-8 px-4 rounded-full bg-indigo-500 text-xs font-medium">Save</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {a.campaign_type === "whitepaper" ? <FileText className="w-4 h-4 text-indigo-300 shrink-0" /> : <Film className="w-4 h-4 text-pink-300 shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{a.title}</p>
            <p className="text-xs text-slate-500 font-mono">{kindOf(a)} · <a href={`/admin/campaigns/${a.id}`} className="text-indigo-400 hover:text-indigo-300">views & leads</a></p>
          </div>
          <button type="button" data-testid={`hub-asset-edit-${a.id}`} onClick={() => setEdit({ title: a.title, description: a.headline || "", thumbnail_path: a.thumbnail_path || "" })} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5"><Pencil className="w-4 h-4" /></button>
          <button type="button" data-testid={`hub-asset-delete-${a.id}`} onClick={del} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
    </li>
  );
};

export const HubAssetsPanel = ({ pid, onChanged }) => {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get(`/programs/${pid}/assets`).then((r) => setList(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = () => { load(); onChanged?.(); };
  return (
    <section className="rounded-lg border border-[#242C3D] bg-[#141A24]/60 mb-12 p-6 space-y-4 sv-rise" data-testid="hub-assets-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-semibold text-lg">Hub content (uploaded directly)</h2>
          <p className="text-xs text-slate-500 mt-1">PDFs, videos and video links that live only in the content hub, with no landing page. They're open to everyone; visitors who came through one of your landing pages or an email link (<span className="font-mono">?email=</span>) are tracked by email.</p>
        </div>
        <span className="font-mono text-xs text-slate-500" data-testid="hub-assets-count">{list ? `${list.length}/${MAX}` : "…"}</span>
      </div>
      {!list ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : <>
      {list.length > 0 && <ul className="space-y-2">{list.map((a) => <AssetRow key={a.id} pid={pid} a={a} onChanged={changed} />)}</ul>}
      {adding ? <AddForm pid={pid} onDone={() => { setAdding(false); changed(); }} onCancel={() => setAdding(false)} /> : (
        <button type="button" data-testid="hub-asset-add-button" disabled={list.length >= MAX} onClick={() => setAdding(true)}
          className="w-full rounded-lg border border-dashed border-[#2f3a50] hover:border-indigo-500 py-5 flex items-center justify-center gap-2 text-sm text-slate-300 transition-colors disabled:opacity-50">
          <Plus className="w-4 h-4" /> Add content to the hub
        </button>
      )}
      </>}
    </section>
  );
};
